export default function Services() {
  const services = [
    {
      icon: 'ri-drop-line',
      title: 'Water Extraction',
      description: 'Rapid removal of standing water using industrial-grade equipment to prevent further damage and mold growth.',
      image: 'https://public.readdy.ai/ai/img_res/71f0496fa69fa1f7e6078d5d29d285ba.jpg'
    },
    {
      icon: 'ri-wind-line',
      title: 'Structural Drying',
      description: 'Advanced drying techniques to restore your property to pre-damage condition with professional dehumidifiers.',
      image: 'https://readdy.ai/api/search-image?query=professional%20structural%20drying%20air%20movers%20and%20dehumidifiers%20set%20up%20in%20water%20damaged%20room%20restoration%20technician%20monitoring%20equipment%20clean%20simple%20background&width=600&height=400&seq=svc-drying&orientation=landscape'
    },
    {
      icon: 'ri-virus-line',
      title: 'Mold Remediation',
      description: 'Complete mold inspection, removal, and prevention services to ensure a healthy indoor environment.',
      image: 'https://readdy.ai/api/search-image?query=mold%20remediation%20specialist%20in%20protective%20gear%20treating%20wall%20mold%20in%20residential%20property%20professional%20restoration%20clean%20simple%20background&width=600&height=400&seq=svc-mold&orientation=landscape'
    },
    {
      icon: 'ri-flood-line',
      title: 'Flood Damage Restoration',
      description: 'Comprehensive flood cleanup and restoration services for residential and commercial properties.',
      image: 'https://readdy.ai/api/search-image?query=flood%20damage%20restoration%20crew%20cleaning%20flooded%20home%20interior%20with%20pumps%20and%20protective%20gear%20Central%20Valley%20California%20clean%20simple%20background&width=600&height=400&seq=svc-flood&orientation=landscape'
    },
    {
      icon: 'ri-fire-line',
      title: 'Fire & Smoke Damage',
      description: 'Expert restoration after fire incidents, including smoke odor removal and structural repairs.',
      image: 'https://readdy.ai/api/search-image?query=fire%20and%20smoke%20damage%20restoration%20technician%20cleaning%20soot%20from%20walls%20in%20damaged%20home%20professional%20restoration%20equipment%20clean%20simple%20background&width=600&height=400&seq=svc-fire&orientation=landscape'
    },
    {
      icon: 'ri-tools-line',
      title: 'Emergency Repairs',
      description: 'Immediate repairs to prevent further damage, including roof tarping and board-up services.',
      image: 'https://readdy.ai/api/search-image?query=emergency%20roof%20tarping%20and%20board%20up%20service%20after%20storm%20damage%20professional%20contractor%20crew%20residential%20property%20clean%20simple%20background&width=600&height=400&seq=svc-emergency&orientation=landscape'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Water Restoration Services
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Comprehensive water damage restoration solutions for homes and businesses in Merced, CA and surrounding areas
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div key={index} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* SEO editorial content block */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">
              Comprehensive Water Damage Restoration Services in Merced, CA
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Water damage is one of the most common and costly disasters that homeowners and business owners in Merced, California face. Whether caused by a burst pipe, appliance malfunction, roof leak, sewage backup, or seasonal flooding, water intrusion can compromise the structural integrity of your property and create serious health hazards within hours. Indoor-Restore Services provides a full spectrum of <strong>water damage restoration services</strong> designed to address every stage of the damage — from emergency water extraction to complete structural rebuilding.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed">
              Our <strong>water extraction</strong> service uses truck-mounted and portable industrial extractors capable of removing thousands of gallons of standing water quickly and efficiently. Speed is critical — the longer water sits, the deeper it penetrates into flooring, wall cavities, insulation, and structural framing. Our crews are dispatched 24 hours a day, 7 days a week, including holidays, to ensure the fastest possible response anywhere in Merced County.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed">
              Following extraction, our <strong>structural drying</strong> process uses high-velocity air movers and low-grain refrigerant (LGR) dehumidifiers to draw residual moisture out of building materials. We monitor drying progress daily using calibrated moisture meters and thermal imaging cameras, adjusting equipment placement as needed to achieve complete drying per IICRC S500 standards. This scientific approach ensures your property is genuinely dry — not just surface-dry — before any reconstruction begins.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed">
              <strong>Mold remediation</strong> is often a necessary component of water damage restoration, particularly in Merced's warm Central Valley climate where mold can begin colonizing within 24 to 48 hours of water intrusion. Our certified mold remediation technicians identify all affected areas, contain active growth, remove contaminated materials safely, and apply EPA-registered antimicrobial treatments to prevent recurrence. We follow IICRC S520 mold remediation standards on every project.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed">
              For properties affected by fire, our <strong>fire and smoke damage restoration</strong> service addresses not only visible char and soot but also the pervasive smoke odor that penetrates porous materials throughout the structure. We use thermal fogging, ozone treatment, and hydroxyl generators to neutralize odors at the molecular level, combined with thorough cleaning of all affected surfaces and HVAC systems.
            </p>
          </div>
          <div className="space-y-6">
            <div className="bg-teal-50 rounded-2xl p-7 border border-teal-100">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Service Highlights</h4>
              <ul className="space-y-3 text-sm text-gray-700">
                {[
                  'IICRC S500 & S520 certified process',
                  'Thermal imaging moisture detection',
                  'Industrial LGR dehumidifiers',
                  'Truck-mounted water extractors',
                  'EPA-registered antimicrobial treatments',
                  'Direct insurance billing available',
                  '60-minute average response time',
                  'Full reconstruction & rebuild services',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-teal-600 mt-0.5 flex-shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-900 rounded-2xl p-7 text-white">
              <h4 className="text-lg font-bold mb-2">Water Damage Emergency?</h4>
              <p className="text-gray-300 text-sm mb-5 leading-relaxed">
                Every minute counts. Call our 24/7 emergency line now for immediate dispatch to your Merced County property.
              </p>
              <a
                href="tel:2094512288"
                className="flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-3 rounded-lg font-semibold hover:bg-teal-500 transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                (209) 451-2288
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
